import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Moon, Sun, Menu, X, Terminal } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { label: "Platform", to: "/#platform" },
  { label: "How it Works", to: "/#how" },
  { label: "Live Demo", to: "/#demo" },
  { label: "Pricing", to: "/pricing" },
];

export function Navbar() {
  const { theme, toggle } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const go = (to) => {
    setOpen(false);
    if (to.startsWith("/#")) {
      if (location.pathname !== "/") navigate("/");
      setTimeout(() => {
        const el = document.getElementById(to.slice(2));
        el?.scrollIntoView({ behavior: "smooth" });
      }, 120);
    } else navigate(to);
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[var(--bg)]/80 border-b border-line">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 h-16 flex items-center justify-between">
        <button onClick={() => go("/")} data-testid="nav-logo">
          <Logo />
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <button
              key={n.label}
              onClick={() => go(n.to)}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-sm font-medium text-muted hover:text-ink transition-colors"
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2.5">
          <button
            onClick={toggle}
            data-testid="theme-toggle"
            className="w-9 h-9 flex items-center justify-center rounded-lg border border-line text-muted hover:text-cyber transition-colors"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>

          {user ? (
            <Button
              data-testid="nav-console-btn"
              onClick={() => navigate("/dashboard")}
              className="hidden sm:inline-flex bg-cyber text-[#0B0E14] hover:bg-cyber-dim font-semibold"
            >
              <Terminal size={16} className="mr-1.5" /> Launch Console
            </Button>
          ) : (
            <>
              <Button
                variant="ghost"
                data-testid="nav-signin-btn"
                onClick={() => navigate("/login")}
                className="hidden sm:inline-flex text-ink hover:text-cyber"
              >
                Sign In
              </Button>
              <Button
                data-testid="nav-getstarted-btn"
                onClick={() => navigate("/signup")}
                className="hidden sm:inline-flex bg-cyber text-[#0B0E14] hover:bg-cyber-dim font-semibold"
              >
                Start Free
              </Button>
            </>
          )}

          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden w-9 h-9 flex items-center justify-center rounded-lg border border-line text-ink"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-line bg-[var(--surface)] px-4 py-4 space-y-2">
          {NAV.map((n) => (
            <button
              key={n.label}
              onClick={() => go(n.to)}
              data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className="block w-full text-left py-2 text-ink font-medium"
            >
              {n.label}
            </button>
          ))}
          <div className="flex gap-2 pt-2">
            {user ? (
              <Button onClick={() => go("/dashboard")} className="flex-1 bg-cyber text-[#0B0E14] font-semibold">
                Launch Console
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => go("/login")} className="flex-1 border-line">Sign In</Button>
                <Button onClick={() => go("/signup")} className="flex-1 bg-cyber text-[#0B0E14] font-semibold">Start Free</Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
